<template>
    <div class="na-form">
        <div class="staBtTitle">
            <div class="staTitle col">Import History — Batch Detail</div>
        </div>

        <div v-if="loadError" style="color: red; margin-bottom: 12px;">
            {{ loadError }}
        </div>

        <div v-if="batch" class="batch-header">
            <DxButton text="Back to Import History" icon="back" @click="goBack" style="margin-bottom: 16px;" />

            <table class="header-table">
                <tbody>
                    <tr>
                        <th>Batch Id</th>
                        <td>{{ batch.IbId }}</td>
                        <th>Status</th>
                        <td><span :style="statusStyle(batch.IbStatus)">{{ batch.IbStatus }}</span></td>
                    </tr>
                    <tr>
                        <th>Created On</th>
                        <td>{{ formatDate(batch.IbCreatedOn) }}</td>
                        <th>Created By</th>
                        <td>{{ batch.IbCreatedBy }}</td>
                    </tr>
                    <tr>
                        <th>File</th>
                        <td>{{ batch.IbFileId }}</td>
                        <th>Row Count</th>
                        <td>{{ batch.IbRowCount }}</td>
                    </tr>
                    <tr v-if="batch.IbUndoneOn">
                        <th>Undone On</th>
                        <td>{{ formatDate(batch.IbUndoneOn) }}</td>
                        <th>Undone By</th>
                        <td>{{ batch.IbUndoneBy }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="rows.length > 0" style="margin-top: 24px;">
            <DxDataGrid
                :data-source="rows"
                :show-borders="true"
                :row-alternation-enabled="true"
                :column-auto-width="true"
                :filter-row-visible="true">

                <DxColumn data-field="IbrImportKey" caption="Import Key" />
                <DxColumn data-field="IbrRowType" caption="Type" cell-template="rowTypeTemplate" />
                <DxColumn data-field="IbrAction" caption="Action" cell-template="actionTemplate" />
                <DxColumn data-field="CurrentTitle" caption="Current Title" cell-template="titleTemplate" />
                <DxColumn caption="Reference Id" cell-template="refIdTemplate" />
                <DxColumn data-field="ModifiedSinceImport" caption="Modified Since Import" cell-template="modifiedTemplate" data-type="number" />
                <DxColumn data-field="IbrError" caption="Error" cell-template="errorTemplate" />
                <DxColumn data-field="IbrChangedOn" caption="Imported On" data-type="datetime" format="dd.MM.yyyy HH:mm" />

                <template #rowTypeTemplate="{ data }">
                    <span :style="data.value === 'Link' ? 'color: #004085; background: #cce5ff; padding: 2px 8px; border-radius: 4px;' : 'color: #155724; background: #d4edda; padding: 2px 8px; border-radius: 4px;'">
                        {{ data.value }}
                    </span>
                </template>

                <template #actionTemplate="{ data }">
                    <span :style="actionStyle(data.value)">{{ data.value }}</span>
                </template>

                <template #titleTemplate="{ data }">
                    <span>{{ data.value || '—' }}</span>
                </template>

                <template #refIdTemplate="{ data }">
                    <span>{{ (data.row.data.IbrRowType || '').toLowerCase() === 'link' ? (data.row.data.IbrTliId || '—') : (data.row.data.IbrTopicId || '—') }}</span>
                </template>

                <template #modifiedTemplate="{ data }">
                    <span v-if="data.row.data.IbrError" style="color: #6c757d;">—</span>
                    <span v-else-if="data.value === 1" style="color: #721c24; background: #f8d7da; padding: 2px 8px; border-radius: 4px; font-weight: 600;">Modified</span>
                    <span v-else style="color: #6c757d;">—</span>
                </template>

                <template #errorTemplate="{ data }">
                    <span v-if="data.value" style="color: red;">{{ data.value }}</span>
                    <span v-else style="color: #6c757d;">—</span>
                </template>

            </DxDataGrid>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid'
import { DxButton } from 'devextreme-vue/button'

export default {
    name: 'ImportHistoryDetailVx',
    components: {
        DxDataGrid,
        DxColumn,
        DxButton
    },
    props: {
        ibId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            batch: null,
            rows: [],
            loadError: null
        }
    },
    created() {
        const id = parseInt(this.ibId, 10)
        if (!id || id <= 0) {
            this.loadError = 'Invalid batch id'
            return
        }
        axios.get('WebApi.asmx/api/topicImportService/getImportBatchDetail?ibId=' + id)
            .then(res => {
                if (res.data && res.data.success) {
                    this.batch = res.data.data.batch || null
                    this.rows = res.data.data.rows || []
                } else {
                    this.loadError = (res.data && res.data.errorMessage) || 'Failed to load batch detail'
                }
            })
            .catch(err => {
                this.loadError = 'Failed to load batch detail: ' + (err.message || err)
            })
    },
    methods: {
        goBack() {
            this.$router.push({ name: 'ImportHistoryVx' })
        },
        formatDate(val) {
            if (!val) return '—'
            try {
                const d = new Date(val)
                return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
            } catch {
                return val
            }
        },
        statusStyle(status) {
            switch (status) {
                case 'Success':  return 'color: #155724; background: #d4edda; padding: 2px 8px; border-radius: 4px;'
                case 'Partial':  return 'color: #856404; background: #fff3cd; padding: 2px 8px; border-radius: 4px;'
                case 'Failed':   return 'color: #721c24; background: #f8d7da; padding: 2px 8px; border-radius: 4px;'
                case 'Undone':   return 'color: #383d41; background: #e2e3e5; padding: 2px 8px; border-radius: 4px;'
                default:         return 'padding: 2px 8px;'
            }
        },
        actionStyle(action) {
            switch (action) {
                case 'New':    return 'color: #004085; background: #cce5ff; padding: 2px 8px; border-radius: 4px;'
                case 'Update': return 'color: #856404; background: #fff3cd; padding: 2px 8px; border-radius: 4px;'
                case 'Skip':   return 'color: #383d41; background: #e2e3e5; padding: 2px 8px; border-radius: 4px;'
                default:       return 'padding: 2px 8px;'
            }
        }
    }
}
</script>

<style scoped>
.na-form {
    padding: 16px;
}
.batch-header {
    margin-bottom: 16px;
}
.header-table {
    border-collapse: collapse;
    margin-bottom: 8px;
}
.header-table th,
.header-table td {
    padding: 4px 16px 4px 4px;
    text-align: left;
    vertical-align: top;
}
.header-table th {
    font-weight: 600;
    color: #555;
    white-space: nowrap;
}
</style>
